/**
 * Mappings d'erreurs techniques → messages lisibles par l'utilisateur.
 *
 * Les erreurs backend (Django, PostgreSQL, Python) contiennent des détails
 * techniques qui ne doivent jamais être exposés tels quels dans l'UI.
 * Ce module les transforme en messages clairs et actionnables.
 */

const PATTERNS = [
  // ── Erreurs de format / type Python ──────────────────────────────────────
  [/NoneType.*iterable|not.*iterable/i,
    "Format de fichier incorrect ou système de coordonnées mal sélectionné. Vérifiez que le fichier correspond au système source choisi."],

  [/invalid literal|could not convert|ValueError/i,
    "Valeur non numérique détectée. Vérifiez que toutes les coordonnées sont des nombres valides."],

  [/index out of range|list index/i,
    "Le fichier semble incomplet ou mal formaté. Vérifiez le nombre de colonnes par ligne."],

  [/object has no attribute/i,
    "Données inattendues dans le fichier. Vérifiez le format et le système de coordonnées."],

  // ── Erreurs PostgreSQL / contraintes base de données ─────────────────────
  [/duplicate key.*unique constraint|unique constraint.*duplicate|unique.*violation/i,
    "Un fichier avec ce nom existe déjà dans votre espace. Renommez-le ou supprimez l'existant."],

  [/foreign key|violates.*constraint/i,
    "Opération impossible. Vérifiez vos données et réessayez."],

  // ── Erreurs d'authentification ────────────────────────────────────────────
  [/not_authenticated|non authentifi|session.*expir/i,
    "Votre session a expiré. Veuillez vous reconnecter."],

  // ── Erreurs de conversion interne (préfixe Django côté Madjeo) ───────────
  [/Erreur de conversion interne/i,
    "La conversion a échoué. Vérifiez le format du fichier et le système de coordonnées sélectionné."],

  [/Erreur dans les données ou types/i,
    "Format de fichier incorrect ou système de coordonnées incompatible. Consultez le guide format pour préparer votre fichier."],

  // ── Erreurs réseau / serveur ──────────────────────────────────────────────
  [/network|ECONNREFUSED|fetch|ERR_/i,
    "Impossible de joindre le serveur. Vérifiez votre connexion et réessayez."],

  [/timeout|ETIMEDOUT/i,
    "La requête a pris trop de temps. Réessayez dans quelques instants."],

  [/Erreur 500|Internal Server Error|500/,
    "Erreur interne du serveur. Réessayez ou contactez le support si le problème persiste."],

  [/Erreur 413|413/,
    "Fichier trop volumineux pour être traité par le serveur."],
];

/**
 * Transforme un message d'erreur technique en message compréhensible.
 *
 * @param {string|Error|unknown} raw      Message brut (string, Error, ou objet quelconque)
 * @param {string}               fallback Message affiché si aucun pattern ne correspond
 * @returns {string}
 *
 * @example
 * notify(sanitizeError(err.message, "La conversion a échoué."));
 */
export function sanitizeError(raw, fallback = 'Une erreur est survenue. Réessayez.') {
  const msg = typeof raw === 'string'
    ? raw
    : raw instanceof Error
      ? raw.message
      : String(raw ?? '');

  for (const [pattern, friendly] of PATTERNS) {
    if (pattern.test(msg)) return friendly;
  }

  return fallback;
}
